import './polyfills';
import React from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import * as Sentry from '@sentry/react';
import { BrowserTracing } from '@sentry/tracing';
import App from './App';
import cookies from './utils/cookies';
import { Router } from 'react-router-dom';
import { createBrowserHistory } from 'history';
import ReactGA from 'react-ga';
import AppError from './pages/AppError';
import { Provider } from 'react-redux';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import store from './store';
import { initialState } from './slices/sessionSlice';

const history = createBrowserHistory();

ReactGA.initialize(process.env.REACT_APP_GA_KEY, { debug: false });
ReactGA.plugin.require('linkid');
ReactGA.plugin.require('displayfeatures');

const sentry_envs = {
    prod: 'production',
    stage: 'staging',
    dev: 'development'
};

if (['prod'].includes(process.env.REACT_APP_ENV)) {
    Sentry.init({
        dsn: process.env.REACT_APP_SENTRY_DSN,
        environment: sentry_envs[process.env.REACT_APP_ENV] || 'unknown',
        debug: false,
        integrations: [
            new BrowserTracing({
                routingInstrumentation: Sentry.reactRouterV5Instrumentation(history)
            })
        ],

        // We recommend adjusting this value in production, or using tracesSampler
        // for finer control
        tracesSampleRate: 1.0
    });
}

axios.defaults.baseURL = process.env.REACT_APP_API_URL;
axios.defaults.headers.common['Content-Type'] = 'application/json; charset=utf-8';

const getNewToken = async () => {
    return axios
        .request({
            url: `/unauthorized/refresh`,
            method: 'POST',
            data: {
                refreshtoken: cookies.get('refresh_token'),
                username: initialState.session['cognito:username']
            }
        })
        .then((resp) => resp.data);
};

axios.interceptors.request.use(
    (config) => {
        let token = cookies.get('auth_token');

        if (token && !config.url.includes('s3.amazonaws.com')) {
            config.headers.common['Authorization'] = token;
        }
        return config;
    },
    (error) => Promise.reject(error)
);

axios.interceptors.response.use(
    (response) => response,
    async (error) => {
        if (!error.response) {
            return Promise.reject(error);
        }

        if (
            !error.response.config.url.includes(process.env.REACT_APP_API_URL) &&
            error.response.config.baseURL !== process.env.REACT_APP_API_URL
        ) {
            return Promise.reject(error);
        }
        if (error.response.status === 400 && error.response.config.url.includes('/refresh')) {
            cookies.remove('auth_token', { path: '/' });
            cookies.remove('refresh_token', { path: '/' });
            cookies.remove('access_key', { path: '/' });
            history.replace({
                pathname: '/login',
                state: { nextPathname: window.location.pathname, reason: 'session_expired' }
            });
        }

        if (error.response.status === 401) {
            if (cookies.get('refresh_token')) {
                let tokenResp = await getNewToken();
                let idToken = tokenResp.AuthenticationResult.IdToken;
                cookies.set('auth_token', idToken, { path: '/', maxAge: 3600 });
                return axios(error.config);
            }
            cookies.remove('auth_token', { path: '/' });
            cookies.remove('refresh_token', { path: '/' });
            cookies.remove('access_key', { path: '/' });
            history.replace({
                pathname: '/login',
                state: { nextPathname: window.location.pathname, reason: 'session_expired' }
            });
        }

        return Promise.reject(error);
    }
);

export const isLoggedIn = () => {
    return new Promise(async (resolve, reject) => {
        if (!cookies.get('auth_token') && cookies.get('refresh_token')) {
            try {
                let tokenResp = await getNewToken();
                let idToken = tokenResp.AuthenticationResult.IdToken;
                cookies.set('auth_token', idToken, { path: '/', maxAge: 3600 });
                resolve(true);
            } catch (e) {
                resolve(false);
            }
        }
        return resolve(!!cookies.get('auth_token'));
    });
};

const AppWrapper = () => {
    return (
        <Sentry.ErrorBoundary fallback={AppError}>
            <Router history={history}>
                <Provider store={store}>
                    <App />
                    <ToastContainer />
                </Provider>
            </Router>
        </Sentry.ErrorBoundary>
    );
};

ReactDOM.render(<AppWrapper />, document.getElementById('root'));
